import { create } from 'zustand'
import { ESTATE_SETTING_DEFAULTS } from './estateSettingFields';
import { TAX_SETTING_TRACK_DEFAULTS } from './taxSettingFields.js';

// Define the initial state
// Exported so standalone pages that need a full, validation-passing base
// payload without a live plan (e.g. the Efficient Frontier analysis page)
// can spread it rather than duplicating every field the simulation
// serializer requires.
export const initialPresetObject = {
  plan_title: "Plan",
  plan_date: new Date().toISOString().split('T')[0],
  two_person_simulation: false,
  person_one_age: 65, 
  person_two_age: 65,
  auto_death_age: false,
  person_one_death_age: 95,
  person_two_death_age: 95, 
  historic_asset_return_data: {},
  forward_asset_return_data: {},
  mortality_data: {},
  data_start_year: 1870,
  data_end_year: 2025,
  currency_set: 'USD',
  geographic_set: 'DOMESTIC',
  equity_tax: 0,
  bond_tax: 0,
  draw_tax: 0,
  base_tax_rate: 0,
  higher_tax_rate: 0,
  rental_income_tax: 0,
  business_dividend_tax: null,
  higher_draw_tax_threshold: 0,
  additional_tax_rate: 0,
  additional_draw_tax_threshold: 0,
  include_other_income_in_threshold: true,
  apply_tiered_tax: true,
  td_withdrawal_mode: 'pcls',
  lifetime_allowance: null,
  tax_free_percentage: 0,
  te_annual_allowance: 0,
  bond_coupon: 3,
  index_bond_coupon: 0.5,
  percentage_bond_return_taxed: 100,
  asset_mix_equity: 100,
  asset_mix_bond: 0,
  asset_mix_index_bond: 0,
  asset_mix_cash: 0,
  // "0": derive the mix from the asset portfolio (the Full Mode default); "1": the mix entered below.
  // The guided modes and flows that set a mix themselves use "1" (see calculator.js).
  asset_mix_source: "0",
  asset_mix_equity_2: 40,
  asset_mix_bond_2: 60,
  asset_mix_index_bond_2: 0,
  asset_mix_cash_2: 0,  
  age_start_transition: 57,
  age_end_transition: 65,
  data_option: "forwardUSD",
  data_direction: "back",
  dynamic_option: "constant",
  withdrawal_order: "GATDTE",
  withdrawal_order_regular: "GATDTETD",
  // "Tax Deferred (to limit)": the band the pension is drawn up to before the GA / TE ('higher' = the higher
  // rate threshold; also 'pa', 'taper', 'additional')
  td_threshold: "higher",
  // td_threshold 'user': the total income up to which (null = the plan's higher rate threshold)
  td_threshold_amount: null,
  fund_fill: "PROPOR",
  calc_safe_level: false,
  use_safe_level: false,
  iteration_type: true,
  withdrawal_percent: 4.0,
  target_withdrawal_percent: 5,
  net_other_income: false,
  yale_weighting: 70,
  vanguard_decrease_floor: 1.5,
  vanguard_increase_ceiling: 5,
  arva_discount_rate: 5.5,
  min_age_tax_deferred_withdrawal: 55,
  person_two_min_age_tax_deferred_withdrawal: 55,
  use_default_return: false,
  default_return_source: 'core',
  equity_nominal_rtn_default: null,
  conventional_bond_nominal_rtn_default: null,
  index_bond_nominal_rtn_default: null,
  cash_nominal_rtn_default: null,
  cpi_default: null,
  equity_risk_premium: 3, 
  reduced_equity_risk_premium: 1,
  cpi_elevated: 5,
  annuity_tax_rate: 0,
  state_pension_tax_rate: 0,
  occupational_pension_tax_rate: 0,
  fees: null,
  apply_fees_to_bond_ladder: false,
  shortfall_tolerance_percent: null,
  insurance_price_scale_percent: null,
  // How an inflation-linked annuity bought outside a pension is grossed up: 'hindsight' / 'fixed'; null = the default
  annuity_tax_gross_up: null,
  insurance_mortality_initial_pct: null,
  insurance_mortality_initial_years: null,
  insurance_mortality_ultimate_pct: null,
  insurance_mortality_ultimate_year: null,
  insurance_mortality_taper_from_age: null,
  insurance_survivor_uplift_pct: null,
  insurance_survivor_uplift_years: null,
  // Nullable sparse-override: null means "auto-track the live default"
  // (fetched from /api/getsimulationdata/), same pattern as fees.
  spread: null,
  equity_alpha: null,
  fixed_income_alpha: null,
  start_simulation_age: 65,
  person_one_retire_age: 65,
  person_two_retire_age: 65,
  end_simulation_age: 95,
  year_retire: 65,
  circular_simulation: "1",
  block_size: 5,
  monte_carlo_iterations: 10000,
  income_inflows: [],
  extraordinary_inflows: [],
  state_pension: [],
  occupational_pension: [],
  residential_property: [],
  rental_property: [],
  // Businesses / farms held outside the portfolio (as a rental property, without stamp duty)
  business_asset: [],
  gift_recurring: [],
  gift_oneoff: [],
  interest_free_loan: [],
  discount_gift_trust: [],
  whole_of_life_policy: [],
  gift_withdrawal_order: 'GATETD',
  ...ESTATE_SETTING_DEFAULTS,
  annuity_pension: [],
  index_bond_ladder_purchase: [],
  contribution_inflows: [],
  income_requirements: [],
  ladder_income_requirements: [],
  flex_income_requirements: [],
  asset_portfolio: [],
  asset_deduction: [{equity_ga: 0, bonds_ga: 0, index_ga: 0, cash_ga: 0, equity_te: 0, bonds_te: 0, index_te: 0, cash_te: 0, equity_td: 0, bonds_td: 0, index_td: 0, cash_td: 0}],
  asset_deduction_person_two: [{equity_ga: 0, bonds_ga: 0, index_ga: 0, cash_ga: 0, equity_te: 0, bonds_te: 0, index_te: 0, cash_te: 0, equity_td: 0, bonds_td: 0, index_td: 0, cash_td: 0}],
  apply_tax_to_inflation: false,
  bond_ladder: [],
  bond_matching_option: "0",
  ladder_tax: 0,
  ladder_cash_rate: 0,
  roll_up_unspent_coupons: false,
  // Nullable sparse-override: null means "auto-track the live default"
  // (fetched from /api/getsimulationdata/, per-currency), same pattern as
  // fees etc. — see the NullableNumberField usages in calculator.js.
  annuity_price_scale: null,
  asset_mix_changes: [],
  bond_portfolio: [],
  ga_portfolio_taxable_yield_eq: null,
  ga_portfolio_taxable_yield_fi: null,
  ga_portfolio_taxable_yield_index: null,
  ga_tax_approach: '0',
  bond_tax_approach: '0',
  ignore_expenditure_floor: false,
  do_not_run_safe_calc: false,
  show_max_percentile: false,
  annuity_age_min: 55,
  annuity_age_max: 85,
  income_tax_free_allowance: null,
  ...TAX_SETTING_TRACK_DEFAULTS,
  personal_allowance_taper_threshold: null,
  personal_allowance_taper_rate: null,
  apply_personal_allowance_taper: true,
  withdrawal_tax_free_allowance_threshold: 0,
  withdrawal_tax_free_allowance_rate: 0,
  additional_income_tax_threshold: 0,
  lump_sum_allowance: 0,
  auto_shift_ga_te: true,
  apply_mpaa_rule: true,
  mpaa: 10000,
  tax_situation: "notax",
};

// Create Zustand store
export const usePresetStore = create((set) => ({
  presetObject: initialPresetObject,
  planId: null, // Add planId to the global store
  // "Skip saving for now" — behaves like logged-out (no autosave, no forced
  // entry-flow prompt) while still logged in. Shared across Plan Simulator
  // and Bond Ladder like planId, since they already share one "currently
  // open plan" concept.
  guestMode: false,
  autoRunSimulation: false,
  // Which of the three Plan Simulator pages (Full/Decumulation/Accumulation)
  // the current presetObject reflects — kept here rather than as local
  // component state because Calculator unmounts when the user navigates to
  // Bond Ladder (or anywhere else) and back, and on remount there's no
  // other way to tell "the route now implies a different mode than the
  // one the fields were last reset for". null means nothing has been
  // committed yet this session (fresh load — nothing to protect).
  lastCommittedUxModel: null,
  // Currency the plan's "track the default" fields (tax overrides, GA yields,
  // return defaults, cpi_elevated, ERP) were last reset for by the calculator's
  // start-up data load. Loading a saved plan stamps it with that plan's
  // currency so the start-up load leaves the saved values alone; only a
  // genuine currency change (or a fresh/new plan, null) triggers the reset.
  lastResetCurrency: null,
  // The visitor's regional defaults (currency_set, geographic_set, default_return_source), set by the pages
  // once the location is known (UK: GBP / global / FCA returns) and reapplied by resetPresetObject - so closing
  // a plan, or starting a new one, keeps the visitor's currency rather than falling back to the built-in USD.
  regionDefaults: null,

  setRegionDefaults: (defaults) => set(() => ({
    regionDefaults: defaults,
  })),

  // Function to set presetObject
  setPresetObject: (newData) => set((state) => ({
    presetObject: {
      ...state.presetObject,
      ...newData,
    },
  })),

  // Function to reset presetObject
  resetPresetObject: () => set((state) => ({
    lastResetCurrency: null,
    presetObject: {
      historic_asset_return_data: state.presetObject.historic_asset_return_data,
      forward_asset_return_data: state.presetObject.forward_asset_return_data,
      mortality_data: state.presetObject.mortality_data,
      ...initialPresetObject,
      ...(state.regionDefaults || {}),
      historic_asset_return_data: state.presetObject.historic_asset_return_data,
      forward_asset_return_data: state.presetObject.forward_asset_return_data,
      mortality_data: state.presetObject.mortality_data,
    },
  })),

  // Function to set planId
  setPlanId: (planId) => set(() => ({
    planId: planId,
  })),

  // Function to reset planId
  resetPlanId: () => set(() => ({
    planId: null,
  })),

  setGuestMode: (guestMode) => set(() => ({
    guestMode: guestMode,
  })),

  resetGuestMode: () => set(() => ({
    guestMode: false,
  })),

  setLastResetCurrency: (currency) => set(() => ({
    lastResetCurrency: currency,
  })),

  setLastCommittedUxModel: (mode) => set(() => ({
    lastCommittedUxModel: mode,
  })),

  triggerAutoRunSimulation: () => set(() => ({
    autoRunSimulation: true,
  })),

  resetAutoRunSimulation: () => set(() => ({
    autoRunSimulation: false
  })),

}));

